import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { acceptInvitation, validateApiResponse } from '../../../services';
import { ACCEPTANCE_STEP, getAcceptanceStep } from '../utils/getAcceptanceStep.js';

const mapApiAcceptedToUi = (accepted) => ({
  workspaceName: accepted.workspace_name ?? accepted.workspaceName,
  firstStudio: accepted.first_accessible_studio ?? accepted.firstAccessibleStudio ?? null,
});

/**
 * Drives the login-before-accept sequence of an invitation link (the rule itself lives in
 * `getAcceptanceStep`). Confirming while signed out calls `onSignInRequired` and accepts
 * nothing; the acceptance runs only once the session is authenticated, exactly once.
 *
 * @param {object} options
 * @param {string} options.token
 * @param {string|null} options.linkState One of `LINK_STATE`.
 * @param {boolean} options.isAuthenticated
 * @param {boolean} options.authLoading
 * @param {boolean} [options.resumeConfirm] The recipient pressed Accept before being sent to sign in (never taken from the URL).
 * @param {() => void} options.onSignInRequired Host action: go to the sign-in page, then come back.
 */
export const useAcceptInvitation = ({
  token,
  linkState,
  isAuthenticated,
  authLoading,
  resumeConfirm = false,
  onSignInRequired,
}) => {
  const [confirmRequested, setConfirmRequested] = useState(resumeConfirm);
  const [accepted, setAccepted] = useState(null);
  const [error, setError] = useState(null);
  const startedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const step = getAcceptanceStep({ linkState, confirmRequested, authLoading, isAuthenticated });

  useEffect(() => {
    if (step === ACCEPTANCE_STEP.SIGN_IN && !startedRef.current) {
      startedRef.current = true;
      onSignInRequired();
      return;
    }
    if (step !== ACCEPTANCE_STEP.ACCEPT || startedRef.current) return;
    startedRef.current = true;
    acceptInvitation(token, { authenticated: isAuthenticated })
      .then((res) => {
        if (mountedRef.current) setAccepted(mapApiAcceptedToUi(validateApiResponse(res).body));
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        startedRef.current = false;
        setConfirmRequested(false);
        setError(err);
      });
  }, [step, token, isAuthenticated, onSignInRequired]);

  const confirm = useCallback(() => {
    setError(null);
    setConfirmRequested(true);
  }, []);

  return useMemo(
    () => ({
      confirm,
      confirming: confirmRequested && accepted === null,
      accepted,
      error,
    }),
    [confirm, confirmRequested, accepted, error],
  );
};
